import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { formatRelativeTime, formatTimestamp } from '@/lib/formatters';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { getCachedData, setCachedData } from '@/lib/cache';

const PAGE_SIZE = 20;

type PageNavigationRecord = {
  userId?: string;
  created?: number;
  createdDate?: string;
  pathname?: string;
  search?: string;
  country?: string;
  ipAddress?: string;
  userAgent?: string;
  eventType?: string;
  eventSource?: string;
  eventPhase?: string;
  operation?: string;
  status?: string;
  httpStatus?: number;
  durationMs?: number;
  payload?: string;
  [key: string]: unknown;
};

type PageNavigationResponse = {
  userId: string;
  records: PageNavigationRecord[];
  totalCount: number;
  lastKey?: {
    userId: string;
    created: number;
  };
  hasMore: boolean;
};

export default function PageNavigationPage() {
  const { userId } = useParams<{ userId: string }>();
  const authenticatedFetch = useAuthenticatedFetch();
  const [navigationHistory, setNavigationHistory] = useState<(string | null)[]>(
    [null],
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [records, setRecords] = useState<PageNavigationRecord[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [nextPageKey, setNextPageKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();

    async function fetchNavigation() {
      try {
        const currentLastKey = navigationHistory[currentPageIndex];

        // Check cache first
        const cacheKey = `nav_${userId}_${currentLastKey ?? 'first'}`;
        const cachedData = getCachedData<PageNavigationResponse>(cacheKey);

        if (cachedData) {
          setRecords(cachedData.records);
          setTotalCount(cachedData.totalCount);
          setNextPageKey(
            cachedData.lastKey ? JSON.stringify(cachedData.lastKey) : null,
          );
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);

        // Build URL with lastKey if present
        let url = `users/${userId}/navigation?limit=${PAGE_SIZE}`;
        if (currentLastKey) {
          url += `&lastKey=${encodeURIComponent(currentLastKey)}`;
        }

        const response = await authenticatedFetch(url, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch navigation (${response.status})`);
        }

        const payload = (await response.json()) as PageNavigationResponse;
        if (controller.signal.aborted) {
          return;
        }

        // Store in cache
        setCachedData(cacheKey, payload);

        setRecords(payload.records);
        setTotalCount(payload.totalCount);
        setNextPageKey(
          payload.lastKey ? JSON.stringify(payload.lastKey) : null,
        );
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error(fetchError);
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Unable to fetch page navigation.',
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchNavigation();

    return () => controller.abort();
  }, [authenticatedFetch, userId, currentPageIndex, navigationHistory]);

  const totalPages = totalCount
    ? Math.max(Math.ceil(totalCount / PAGE_SIZE), 1)
    : null;
  const hasNextPage = !!nextPageKey;
  const hasPreviousPage = currentPageIndex > 0;

  const handleNextPage = () => {
    if (!nextPageKey) return;

    // Add the next page key to navigation history if it's not already there
    if (currentPageIndex === navigationHistory.length - 1) {
      setNavigationHistory([...navigationHistory, nextPageKey]);
    }
    setCurrentPageIndex(currentPageIndex + 1);
  };

  const handlePreviousPage = () => {
    if (currentPageIndex === 0) return;
    setCurrentPageIndex(currentPageIndex - 1);
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      <Breadcrumb
        items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Users', to: '/users' },
          { label: userId || 'User', to: `/users/${userId}` },
          { label: 'Navigation' },
        ]}
      />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Page navigation
          </h1>
          <p className="text-muted-foreground">
            Full navigation history for user{' '}
            <span className="font-mono">{userId}</span>
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to={`/users/${userId}`}>Back to user</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Navigation history</CardTitle>
          <CardDescription>
            Showing {records.length} activity events on page{' '}
            {currentPageIndex + 1}
            {totalCount ? ` (${totalCount.toLocaleString()} total)` : ''}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-4">
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Operation / Path</TableHead>
                  <TableHead>Phase / Status</TableHead>
                  <TableHead>Payload</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>User Agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record, index) => {
                  const fullPath = `${record.pathname || ''}${record.search || ''}`;
                  const isBackendEvent =
                    record.eventType === 'backend_operation';
                  const operationOrPath =
                    record.operation || fullPath || '—';
                  const phaseStatus = [record.eventPhase, record.status]
                    .filter((value) => !!value)
                    .join(' / ');
                  const payloadPreview =
                    record.payload && record.payload.length > 160
                      ? `${record.payload.slice(0, 160)}...`
                      : record.payload;
                  return (
                    <TableRow key={index}>
                      <TableCell className="font-mono">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(record.created)}
                          </span>
                          <span className="text-xs">
                            {record.created
                              ? new Date(record.created).toLocaleString()
                              : '—'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={isBackendEvent ? 'default' : 'outline'}>
                          {isBackendEvent ? 'Backend' : 'Page'}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-sm truncate font-mono text-xs">
                        {operationOrPath}
                      </TableCell>
                      <TableCell className="max-w-xs truncate font-mono text-xs">
                        {phaseStatus || '—'}
                        {typeof record.durationMs === 'number' && (
                          <span className="ml-1 text-muted-foreground">
                            ({record.durationMs}ms)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-sm truncate font-mono text-xs text-muted-foreground">
                        {payloadPreview || '—'}
                      </TableCell>
                      <TableCell>
                        {record.country ? (
                          <Badge variant="outline">{record.country}</Badge>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {record.ipAddress || '—'}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-xs text-muted-foreground">
                        {record.userAgent || '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableCaption>
                {loading && 'Loading navigation…'}
                {error && <span className="text-destructive">{error}</span>}
                {!loading &&
                  !error &&
                  records.length === 0 &&
                  'No records found.'}
              </TableCaption>
            </Table>
          </div>
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePreviousPage}
              disabled={!hasPreviousPage || loading}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPageIndex + 1}
              {totalPages ? ` of ${totalPages}` : ''}
            </div>
            <Button
              variant="outline"
              onClick={handleNextPage}
              disabled={!hasNextPage || loading}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
